import { clsx } from 'clsx';
import { useMemo } from 'react';
import { useMedia } from '#hooks/use-media';
import { MenuItem, MenuItemSpacer } from './components';
import { DualNavContext, type DualNavContextType } from './contexts/dual-nav-context';
import styles from './dual-nav.module.css';

type Slot = React.ReactNode | ((context: DualNavContextType) => React.ReactNode);

interface DualNavProps {
  switcherSlot?: Slot;
  mainMenuSlot?: Slot;
  footerMenuSlot?: Slot;
  logoSlot?: Slot;
  /**
   * The width state of the dual nav.
   * - collapsed: The dual nav is collapsed.
   * - expanded: The dual nav is expanded.
   * - responsive: The dual nav is responsive to the screen size and collapses when the screen is smaller than the small screen breakpoint.
   */
  widthState?: DualNavContextType['widthState'] | 'responsive';
  className?: string;
}

function DualNav({
  switcherSlot,
  mainMenuSlot,
  footerMenuSlot,
  logoSlot,
  widthState = 'responsive',
  className,
}: DualNavProps) {
  const isSmallScreen = useMedia('sm');
  const smallScreenWidthState = isSmallScreen ? 'collapsed' : 'expanded';
  const widthStateValue = widthState === 'responsive' ? smallScreenWidthState : widthState;
  const contextValue = useMemo(() => ({ widthState: widthStateValue }), [widthStateValue]);

  const dualNavClassName = clsx(
    styles.dualNav,
    widthStateValue === 'collapsed' ? styles.collapsed : styles.expanded,
    className
  );

  return (
    <DualNavContext.Provider value={contextValue}>
      <nav className={dualNavClassName}>
        {switcherSlot ? (
          <div className={styles.switcherSlot}>
            {typeof switcherSlot === 'function' ? switcherSlot(contextValue) : switcherSlot}
          </div>
        ) : null}
        <div className={styles.mainAndFooterWrapper}>
          <ul className={styles.mainMenuSlot} role={mainMenuSlot ? undefined : 'presentation'}>
            {typeof mainMenuSlot === 'function' ? mainMenuSlot(contextValue) : mainMenuSlot}
          </ul>
          {footerMenuSlot ? (
            <ul className={styles.footerMenuSlot}>
              {typeof footerMenuSlot === 'function' ? footerMenuSlot(contextValue) : footerMenuSlot}
            </ul>
          ) : null}
        </div>
        {logoSlot ? (
          <div className={styles.logoSlot}>
            {typeof logoSlot === 'function' ? logoSlot(contextValue) : logoSlot}
          </div>
        ) : null}
        {/* TODO Submenu: <div className={styles.overlay} /> */}
      </nav>
    </DualNavContext.Provider>
  );
}

DualNav.MenuItem = MenuItem;
DualNav.MenuItemSpacer = MenuItemSpacer;

export { DualNav };
